/*
    Copyright 2022-2026, Techwise Integrated Security, LLC
*/

import React, { useState } from "react";
import { Box, useTheme, IconButton, Tooltip, Typography, Stack, CircularProgress } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Button from "@mui/material/Button";
import Modal from "@mui/material/Modal";
import Backdrop from "@mui/material/Backdrop";
import Fade from "@mui/material/Fade";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import Grid from "@mui/material/Grid";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";

import { convertHeaderDateTimes, delayedAction, toUTC } from "../../utils/Functions";
import { Effect, ModalBase, ServiceLocation, WorkOrder } from "../../utils/Interfaces";
import { createWorkOrder } from "./workOrdersFunctions";
import { useAppDispatch } from "../../app/hooks";
import { AnyAction, Dispatch } from "@reduxjs/toolkit";

export interface CreateWorkOrderModalProps extends ModalBase {
    existingSites: ServiceLocation[];
    onWorkOrderCreated: Effect;
    defaultSite?: ServiceLocation;
}

const CreateWorkOrderModal = (props: CreateWorkOrderModalProps) => {
    const { open, handleClose, existingSites, onWorkOrderCreated, defaultSite } = props;

    const dispatch: Dispatch<AnyAction> = useAppDispatch();

    const [ticketNo, setTicketNo] = useState<string>("");
    const [site, setSite] = useState<ServiceLocation>(defaultSite ? defaultSite : existingSites[0]);

    const darkTheme: boolean = localStorage.getItem("techwiseTheme") === "dark";

    // TODO set the default to NOW
    const [dateReceived, setDateReceived] = useState<string>("");
    const [managerName] = useState<string>("");
    const [problemDescription, setProblemDescription] = useState<string>("");
    const [additionalDescription, setAdditionalDescription] = useState<string>("");
    const [deviceToBeFixed, setDeviceToBeFixed] = useState<string>("");
    const [scissorLiftAvailable, setScissorLiftAvailable] = useState<boolean>(false);

    const [createInProgress, setCreateInProgress] = useState<boolean>(false);

    const theme = useTheme();

    function onClose() {
        handleClose();
        void delayedAction(500, () => {
            setCreateInProgress(false);
            setDateReceived("");
            setProblemDescription("");
            setAdditionalDescription("");
            setDeviceToBeFixed("");
            setScissorLiftAvailable(false);
            setTicketNo("");
            setSite(existingSites[0]);
        });
    }

    function compileWorkOrder(): WorkOrder {
        return {
            status: "UN",
            id: NaN,
            ticket_no: ticketNo,
            date_received: toUTC(new Date(dateReceived)).toISOString(),
            manager_name: managerName,
            problem_description: problemDescription,
            additional_description: additionalDescription,
            device_to_be_fixed: deviceToBeFixed,
            scissor_lift_available: scissorLiftAvailable,
            site: site,
            store_phone: "",
            comments: null,
            created_by: null,
            date_created: null,
            date_submitted: null,
            date_completed: null,
            trip_number: 0,
        };
    }

    return (
        <Modal
            sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
            open={open}
            onClose={onClose}
            closeAfterTransition
            BackdropComponent={Backdrop}
            BackdropProps={{
                timeout: 500,
            }}
        >
            <Fade in={open}>
                <Box
                    sx={{
                        width: "100%",
                        maxHeight: "90vh",
                        maxWidth: "1800px",
                        backgroundColor: theme.palette.background.paper,
                        border: "1px solid #000",
                        boxShadow: theme.shadows[5],
                        padding: theme.spacing(2, 4, 3),
                        margin: theme.spacing(2, 4, 3),
                        zIndex: 1000,
                        overflowY: "auto",
                        position: "relative",
                    }}
                >
                    <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
                        <Typography variant="h5">Manually Enter Work Order</Typography>
                        <Tooltip title="Close" sx={{ mt: -1, mr: -2 }}>
                            <IconButton aria-label="delete" onClick={onClose}>
                                <CloseIcon />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                    <Box
                        sx={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}
                        component="form"
                    >
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <TextField
                                    margin="normal"
                                    required
                                    fullWidth
                                    name="ticket_no"
                                    label="Ticket Number"
                                    type="ticket_no"
                                    id="ticket_no"
                                    value={ticketNo}
                                    onChange={(event) => setTicketNo(event.target.value)}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <Autocomplete
                                    disabled={defaultSite !== null && defaultSite !== undefined}
                                    onChange={(_event, value) => setSite(value ?? existingSites[0])}
                                    id="site"
                                    getOptionKey={(option: ServiceLocation) => option.id}
                                    options={existingSites}
                                    value={site}
                                    getOptionLabel={(site: ServiceLocation) =>
                                        `${site.company.name} ${site.store_no}, ${site.city}, ${site.state}`
                                    }
                                    renderInput={(params) => <TextField margin="normal" required {...params} label="site" />}
                                />
                            </Grid>
                        </Grid>
                        <Box sx={{ marginBottom: 1 }} />
                        <Grid container spacing={2}>
                            <Grid item xs={1}>
                                <Stack direction="column" justifyContent="center">
                                    <Typography sx={{ mt: 2.5 }}>Date Received</Typography>
                                </Stack>
                            </Grid>
                            <Grid item xs={5}>
                                <TextField
                                    id="date_received"
                                    type="datetime-local"
                                    value={convertHeaderDateTimes(dateReceived)}
                                    onChange={(event) => setDateReceived(event.target.value + ":00")}
                                    fullWidth
                                    sx={{ colorScheme: darkTheme ? "dark" : "light", ml: 1.5 }}
                                    inputProps={{ step: 60 }}
                                />
                            </Grid>
                        </Grid>

                        <TextField
                            margin="normal"
                            fullWidth
                            name="device_to_be_fixed"
                            label="Device to be Fixed"
                            type="device_to_be_fixed"
                            id="device_to_be_fixed"
                            value={deviceToBeFixed}
                            onChange={(event) => setDeviceToBeFixed(event.target.value)}
                        />

                        <FormGroup style={{ marginLeft: "auto" }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        id="scissor_lift_available"
                                        name="scissor_lift_available"
                                        onClick={() => setScissorLiftAvailable(!scissorLiftAvailable)}
                                        checked={scissorLiftAvailable}
                                    />
                                }
                                label="scissor lift available"
                            />
                        </FormGroup>
                        <TextField
                            fullWidth
                            margin="normal"
                            id="problem_description"
                            name="problem_description"
                            label="Problem Description"
                            value={problemDescription}
                            multiline
                            rows={6}
                            onChange={(event) => setProblemDescription(event.target.value)}
                        />
                        <TextField
                            margin="normal"
                            fullWidth
                            id="additional_description"
                            label="Additional Description"
                            name="additional_description"
                            value={additionalDescription}
                            multiline
                            rows={6}
                            onChange={(event) => setAdditionalDescription(event.target.value)}
                        />
                        {!createInProgress && (
                            <Button
                                onClick={() => {
                                    let dateReceivedForward = null;
                                    if (dateReceived) {
                                        dateReceivedForward = new Date(
                                            typeof dateReceived === "string" ? new Date(dateReceived) : dateReceived
                                        );
                                        var userTimezoneOffset = dateReceivedForward.getTimezoneOffset() * 60000;
                                        dateReceivedForward = new Date(dateReceivedForward.getTime() - userTimezoneOffset);
                                        dateReceivedForward = dateReceivedForward.toISOString().replace("Z", "");
                                    }

                                    let workOrderData = compileWorkOrder();

                                    setCreateInProgress(true);

                                    createWorkOrder(workOrderData, dispatch, () => {
                                        onWorkOrderCreated();
                                        onClose();
                                        setCreateInProgress(false);
                                    });
                                }}
                                fullWidth
                                variant="contained"
                                sx={{ mt: 3, mb: 2 }}
                                color="primary"
                            >
                                Submit
                            </Button>
                        )}
                        {createInProgress && (
                            <Stack direction="row" justifyContent="center" sx={{ mt: 4, mb: 2 }}>
                                <CircularProgress size={20} />
                            </Stack>
                        )}
                    </Box>
                </Box>
            </Fade>
        </Modal>
    );
};

export default CreateWorkOrderModal;
